import type { Issue } from '../api/DigiobcanApi';
import { __ } from '../utils';
import CheckCircle from '@mui/icons-material/CheckCircle';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ScheduleIcon from '@mui/icons-material/Schedule';
// `Chip` z kniznice je od v2 chip na viacnasobny vyber (`children`, `selected`,
// `onRemove`), nie MUI stitok. Tu ide vedome MUI komponent - rovnako ako
// v `AdminReportCategories`, s ktorym stitok zdiela tvar.
import MuiChip from '@mui/material/Chip';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Stack,
  Tooltip,
  Typography,
  type StatusChipTone,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Stavy nálezu plus `archived`, ktorý nie je stav nálezu, ale **príznak
 * podnetu** (`reports.archived_at`).
 *
 * Do tejto mapy patrí napriek tomu: v stĺpci „Stav podnetu" má archivácia
 * ostatné stavy **prebiť** - úradníkovi je jedno, že archivovaný podnet mal
 * naposledy nepridelený nález, podstatné je, že je z aplikácie vonku. A keď
 * to má byť jeden stĺpec s jednou bodkou, musí to byť jedna mapa; druhé
 * miesto s vlastnou farbou by sa s touto rozišlo.
 *
 * Stavy nálezov (`AdminReportCategories`, karty nálezov) sa tým nemenia -
 * archivácia je vlastnosť podnetu a nálezy si svoje stavy držia.
 */
export type ReportStatus = Issue['status'] | 'archived';

/**
 * Obyčajný objekt, nie `SxProps`: štýly sa v štítku spájajú spreadom
 * (`sx` na `Chip` z ui-react array formu neprijíma) a takto je aj vidieť,
 * čím presne stav do vzhľadu štítku prispieva.
 */
type ChipStyles = {
  backgroundColor: string | ((theme: Theme) => string);
  color: string;
  '&:hover': { backgroundColor: string | ((theme: Theme) => string) };
};

type StatusMeta = {
  title: string;
  icon: React.ReactNode;
  tooltip?: string;
  /** Bodka na bielom podklade - riadok tabuľky, tooltip. */
  dotColor: string;
  /** Bodka na podfarbenom štítku, keď na tinte `dotColor` splynie. */
  chipDotColor?: string;
  /** Podfarbenie štítku kategórie - pozadie aj text v rodine farby stavu. */
  chipStyles: ChipStyles;
  /**
   * Tón pre `StatusChip` z knižnice. Detail podnetu kreslí stav ním a nie
   * `chipStyles`; obe musia vychádzať z tejto jednej mapy, inak by ten istý
   * stav mal v zozname inú farbu ako v detaile.
   */
  tone: StatusChipTone;
};

/**
 * Tichý tint v rodine stavu, nie plná výplň: štítkov je v riadku aj päť a plné
 * farby by preberali pozornosť stĺpcu Lehota, ktorý farbou upozorňuje na
 * termín. Hover treba prepísať tiež - `filled` v téme tam vracia sivú.
 */
const tintedChip = (
  palette: 'blue' | 'warning' | 'error' | 'success',
  tint: number,
  color: string,
): ChipStyles => {
  const background = (theme: Theme) => alpha(theme.palette[palette].main, tint);

  return {
    backgroundColor: background,
    color,
    '&:hover': { backgroundColor: background },
  };
};

/** Neutrálny štítok - stav, ktorý už nič nehlási, a súčtové `+X`. */
export const NEUTRAL_CHIP_STYLES: ChipStyles = {
  backgroundColor: 'neutralSurface',
  color: 'text.secondary',
  '&:hover': { backgroundColor: 'neutralSurface' },
};

/**
 * Jediny zdroj pravdy pre nazov, ikonu a farbu stavu. Farba je nosnou
 * informaciou v stitkoch kategorii ([[AdminReportCategories]]), kde uz na label
 * nie je miesto - preto sa mapovanie drzi tu a nie v kazdom mieste zobrazenia.
 */
export const getReportStatusMeta = (status?: ReportStatus): StatusMeta => {
  switch (status) {
    // Jantárová = čaká na úradníka, modrá = už sa rieši. Farba tak hovorí, čo
    // treba vziať do ruky, a nie iba v akej fáze podnet je; jantárová sa
    // zároveň zhoduje s tým, čím stĺpec Lehota upozorňuje na blížiaci sa termín.
    //
    // Prehodenie na červené „Podané" a jantárový „Duplikát" tu chvíľu bolo
    // a je **vrátené**: v tabuľke má farba hlásiť naliehavosť, a červená patrí
    // duplikátu, ktorý sa zahadzuje. Zoznam sa tým rozchádza s mapou, kde je
    // ten istý stav („Nové") červený - je to vedomé, mapa ostáva ako je.
    case 'open':
      return {
        title: __('Podané'),
        icon: <CheckCircle color='warning' fontSize='small' />,
        tooltip: __('Podnet čaká na pridelenie zodpovednému útvaru.'),
        // `warning.main` je na bielej príliš svetlá na 9px bodku (1,6:1),
        // a „Podané" je najčastejší stav v zozname - preto tmavší krok.
        dotColor: 'warning.dark',
        tone: 'warning',
        // Žltá je jediná farba témy, ktorá text vo svojej rodine neunesie:
        // `warning.extraDark` má na bielej 3,62:1, teda pod AA pre 12px text.
        // Na jantárovom tinte je preto tmavý text a ešte stmavená bodka.
        chipDotColor: 'warning.extraDark',
        chipStyles: tintedChip('warning', 0.3, 'text.primary'),
      };
    case 'accepted':
      return {
        title: __('V riešení'),
        icon: <ScheduleIcon color='info' fontSize='small' />,
        tooltip: __('Podnet bol priradený pracovníkovi a rieši sa.'),
        dotColor: 'blue.main',
        chipStyles: tintedChip('blue', 0.14, 'blue.extraDark'),
        tone: 'info',
      };
    case 'resolved':
      return {
        title: __('Vyriešené'),
        icon: <CheckCircle color='success' fontSize='small' />,
        tooltip: __('Úrad nález uzavrel a zverejnil finálne stanovisko.'),
        dotColor: 'success.main',
        chipStyles: tintedChip('success', 0.16, 'success.dark'),
        tone: 'success',
      };
    case 'duplicate':
      return {
        title: __('Duplikát'),
        icon: <CheckCircle color='error' fontSize='small' />,
        dotColor: 'error.main',
        chipStyles: tintedChip('error', 0.12, 'error.dark'),
        tone: 'error',
      };
    // Čierna, nie sivá: archív nie je vyblednutý ani polovičný stav, je to
    // koniec. Sivá (`text.disabled`) v tejto tabuľke znamená „už sa tým
    // nikto nezaoberá, ale visí to tu" - presunutý nález a neznámy stav.
    // Archivovaný podnet je naopak výsledok rozhodnutia a má sa v stĺpci
    // čítať ako prvý.
    case 'archived':
      return {
        title: __('Archivované'),
        icon: <InventoryIcon fontSize='small' sx={{ color: 'text.primary' }} />,
        tooltip: __(
          'Podnet je vyradený z aplikácie - nevidí ho občan ani jeho autor.',
        ),
        dotColor: 'text.primary',
        chipDotColor: 'text.primary',
        chipStyles: NEUTRAL_CHIP_STYLES,
        tone: 'neutral',
      };
    case 'moved':
      return {
        title: __('Presunuté'),
        icon: <KeyboardDoubleArrowRightIcon color='info' fontSize='small' />,
        dotColor: 'text.disabled',
        chipStyles: NEUTRAL_CHIP_STYLES,
        tone: 'neutral',
      };
    default:
      return {
        title: __('Neznámy stav'),
        icon: <ScheduleIcon color='error' fontSize='small' />,
        dotColor: 'text.disabled',
        chipStyles: NEUTRAL_CHIP_STYLES,
        tone: 'neutral',
      };
  }
};

/** Ticha bodka podla noveho dizajnu tabuliek - bez labelu a bez tooltipu. */
export const ReportStatusDot: React.FC<{
  status?: ReportStatus;
  size?: number;
  /** `chip` = bodka leží na podfarbenom štítku, nie na bielom podklade. */
  on?: 'surface' | 'chip';
  /**
   * Musí sa preposielať: `Chip` si ikonu klonuje a pridáva jej triedu
   * `MuiChip-icon`. Bez tohto prop-u ju komponent zahodí, bodka stratí odsadenie
   * aj zvislé centrovanie z témy a sadne si na hranu štítku.
   */
  className?: string;
}> = ({ status, size = 9, on = 'surface', className }) => {
  const meta = getReportStatusMeta(status);

  return (
    <Box
      className={className}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor:
          on === 'chip' ? (meta.chipDotColor ?? meta.dotColor) : meta.dotColor,
        flexShrink: 0,
      }}
    />
  );
};

/**
 * Farby kolieska riešiteľa (moj-presov#17) - v štítku kategórie aj v páse
 * „Tento nález rieši" v detaile. Výplň je farba bodky stavu na štítku, takže
 * koliesko patrí do rodiny štítku, v ktorom sedí, a nie je to cudzia sivá
 * škvrna. Iniciály biele: tmavšie kroky (`warning.extraDark`, `blue.main`,
 * `success.main`, `error.main`) ich unesú, svetlý tint štítku by nie.
 */
export const reportStatusAvatarSx = (status?: ReportStatus) => {
  const meta = getReportStatusMeta(status);

  return {
    bgcolor: meta.chipDotColor ?? meta.dotColor,
    color: 'common.white',
  };
};

/**
 * Stav ako **podfarbený štítok s bodkou** - presne ten, aký nesú štítky
 * kategórií v administračnom zozname podnetov ([[AdminReportCategories]]).
 *
 * Je to tu, a nie v `AdminReportCategories`, lebo ten štítok potrebuje ešte
 * meranie preplneného riadku (`data-category-chip`, `minWidth: 0`) a to sem
 * nepatrí. Spoločné je to podstatné: tint, bodka a odsadenie ikony - aby ten
 * istý stav nemal v „Mojich podnetoch" iný tvar než v administrácii.
 *
 * `label` sa dopĺňa vtedy, keď má štítok povedať niečo iné než názov stavu -
 * občan v „Mojich podnetoch" vidí vlastnú škálu („Podané", „V riešení"),
 * ktorá na tie isté stavy sadá vlastnými slovami.
 */
export const ReportStatusChip: React.FC<{
  status?: ReportStatus;
  label?: string;
}> = ({ status, label }) => {
  const meta = getReportStatusMeta(status);

  return (
    <MuiChip
      icon={<ReportStatusDot status={status} on='chip' />}
      label={label ?? meta.title}
      sx={{
        ...meta.chipStyles,
        // Tema odsadzuje ikonu o 4 px zlava - hodnota pre 20px MUI ikony,
        // ktore maju bielu plochu v sebe. Bodka ziadnu nema, takze 8 px.
        '& .MuiChip-icon': { marginLeft: '8px', marginRight: '-2px' },
      }}
    />
  );
};

type Props = {
  status?: ReportStatus;
  showLabel?: boolean;
  /**
   * `icon` je pôvodné zobrazenie s MUI ikonou.
   * `dot` je tichšia bodka podľa nového dizajnu tabuliek - ikona v zozname
   * súperila o pozornosť so stĺpcom Lehota, ktorý má farbou upozorňovať.
   */
  variant?: 'icon' | 'dot';
};

const AdminReportStatusItem: React.FC<Props> = ({
  status,
  showLabel = true,
  variant = 'icon',
}) => {
  const { title, icon, tooltip } = React.useMemo(
    () => getReportStatusMeta(status),
    [status],
  );

  return (
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Tooltip title={tooltip || ''}>
        <Stack direction='row' gap={1} alignItems='center'>
          {variant === 'dot' ? <ReportStatusDot status={status} /> : icon}
          {showLabel && <Typography variant='body2'>{title}</Typography>}
        </Stack>
      </Tooltip>
    </Stack>
  );
};

export default AdminReportStatusItem;
